<template>
	<v-data-table
		data-tour="table"
		:headers="headers"
		:items="items"
		:options="options"
		:server-items-length="total"
		:loading="loading"
		class="elevation-0"
		:footer-props="{ 'items-per-page-options': [25, 50, 100] }"
		item-key="_id"
		:hide-default-header="mobileCards"
		:class="{ 'table-cards': mobileCards }"
		@update:options="$emit('update:options', $event)"
	>
		<!-- telefono: ogni riga diventa una card chiave/valore -->
		<template v-if="mobileCards" v-slot:body="{ items: righe }">
			<tbody class="price-cards">
				<tr v-if="righe.length === 0">
					<td :colspan="headers.length" class="px-3 py-1">
						<div class="grey--text text-center py-4">{{ loading ? 'Caricamento...' : 'Nessun elemento' }}</div>
					</td>
				</tr>
				<tr v-for="item in righe" :key="item._id">
					<td :colspan="headers.length" class="px-3 py-1">
						<key-value-card :title="item.title || item.sellerSku || '-'" :fields="campiCard(item)" :image="item.image || ''">
							<template v-slot:top>
								<div class="d-flex align-center">
									<v-btn icon small data-tour="follow" :loading="followingId === item._id" @click="$emit('follow', item)">
										<v-icon :color="item.followed ? 'amber' : 'grey'">{{ item.followed ? 'mdi-star' : 'mdi-star-outline' }}</v-icon>
									</v-btn>
									<v-chip x-small :color="item._item ? 'primary' : 'grey'" text-color="white" class="ml-1">{{ item._item ? 'Associato' : 'Non associato' }}</v-chip>
									<v-chip v-if="item.active === false" x-small color="warning" class="ml-1">Non più attiva</v-chip>
								</div>
							</template>
							<div class="d-flex justify-space-between">
								<span class="card-label">Buybox</span>
								<span>
									<v-chip x-small :color="buyBoxChip(item).color" text-color="white">{{ buyBoxChip(item).text }}</v-chip>
									<span v-if="hasBuyBoxPrice(item)" class="font-weight-bold ml-1">{{ money(item.analysis.buyBoxListingPrice, item.analysis.currency) }}</span>
								</span>
							</div>
							<template v-slot:actions>
								<v-btn small text color="primary" data-tour="analyze" :loading="analyzingId === item._id" :disabled="analyzeDisabled || !item.asin" :title="item.asin ? 'Buy Box e tua offerta di questo prodotto, subito' : 'Manca l’ASIN'" @click="$emit('analyze', item)">
									<v-icon left small>mdi-chart-line</v-icon>
									Analizza ora
								</v-btn>
								<v-btn small color="primary" text data-tour="adjust" :disabled="!hasBuyBoxPrice(item)" @click="$emit('adjust', item)">
									<v-icon left small>mdi-tune-variant</v-icon>
									Aggiusta prezzo
								</v-btn>
							</template>
						</key-value-card>
					</td>
				</tr>
			</tbody>
		</template>

		<template v-slot:item.followed="{ item }">
			<v-btn icon data-tour="follow" :loading="followingId === item._id" @click="$emit('follow', item)">
				<v-icon :color="item.followed ? 'amber' : 'grey'">{{ item.followed ? 'mdi-star' : 'mdi-star-outline' }}</v-icon>
			</v-btn>
		</template>
		<template v-slot:item.image="{ item }">
			<v-img v-if="item.image" :src="item.image" max-height="48" max-width="48" contain class="my-1"></v-img>
			<v-icon v-else color="grey lighten-1">mdi-image-off-outline</v-icon>
		</template>
		<template v-slot:item.title="{ item }">
			<span v-if="item.title">{{ item.title }}</span>
			<span v-else class="grey--text">—</span>
			<div>
				<v-chip x-small :color="item._item ? 'primary' : 'grey'" text-color="white">{{ item._item ? 'Associato' : 'Non associato' }}</v-chip>
				<v-chip v-if="item.active === false" x-small color="warning" class="ml-1">Non più attiva</v-chip>
			</div>
		</template>
		<template v-slot:item.sellerSku="{ item }">
			<div>{{ item.sellerSku }}</div>
			<a v-if="productUrl(item)" :href="productUrl(item)" target="_blank" rel="noopener" class="caption">{{ item.asin }}</a>
			<span v-else class="caption">{{ item.asin || '-' }}</span>
		</template>
		<template v-slot:item.price="{ item }">
			<span v-if="item.price === null || item.price === undefined">-</span>
			<template v-else>
				<span>{{ money(item.price, item.currency) }}</span>
				<!-- il cliente paga prezzo + spedizione: e' il totale che va confrontato con la buybox -->
				<div v-if="ourTotalOf(item) !== null" class="caption" :class="isMoreExpensive(item) ? 'red--text font-weight-bold' : 'grey--text'">
					+ {{ money(ourShippingOf(item), item.currency) }} sped. = {{ money(ourTotalOf(item), item.currency) }}
				</div>
				<div v-else-if="item.analysis && item.analysis.ourStatus === 'no_offer'" class="caption grey--text">offerta non acquistabile ora</div>
			</template>
			<div v-if="item.lastAdjust && item.lastAdjust.at" class="caption grey--text">aggiustato il {{ item.lastAdjust.at | moment('DD/MM HH:mm') }}</div>
		</template>
		<template v-slot:item.buybox="{ item }">
			<v-chip small :color="buyBoxChip(item).color" text-color="white">{{ buyBoxChip(item).text }}</v-chip>
			<div v-if="item.analysis && item.analysis.status === 'error'" class="caption red--text text-truncate" style="max-width: 220px" :title="item.analysis.error">
				{{ item.analysis.error }}
			</div>
		</template>
		<template v-slot:item.buyBoxPrice="{ item }">
			<template v-if="hasBuyBoxPrice(item)">
				<span class="font-weight-bold">{{ money(item.analysis.buyBoxListingPrice, item.analysis.currency) }}</span>
				<div v-if="item.analysis.buyBoxShipping" class="caption grey--text">
					+ {{ money(item.analysis.buyBoxShipping, item.analysis.currency) }} sped. = {{ money(item.analysis.buyBoxLandedPrice, item.analysis.currency) }}
				</div>
				<div v-if="item.analysis.offers" class="caption grey--text">{{ item.analysis.offers }} offerte</div>
			</template>
			<span v-else>-</span>
		</template>
		<template v-slot:item.analysisAt="{ item }">
			<span v-if="item.analysis && item.analysis.at">{{ item.analysis.at | moment('DD/MM/YYYY HH:mm') }}</span>
			<span v-else>-</span>
		</template>
		<template v-slot:item.actions="{ item }">
			<v-btn small text color="primary" data-tour="analyze" :loading="analyzingId === item._id" :disabled="analyzeDisabled || !item.asin" :title="item.asin ? 'Buy Box e tua offerta di questo prodotto, subito' : 'Manca l’ASIN'" @click="$emit('analyze', item)">
				<v-icon left small>mdi-chart-line</v-icon>
				Analizza ora
			</v-btn>
			<v-btn small color="primary" text data-tour="adjust" :disabled="!hasBuyBoxPrice(item)" @click="$emit('adjust', item)">
				<v-icon left small>mdi-tune-variant</v-icon>
				Aggiusta prezzo
			</v-btn>
		</template>
	</v-data-table>
</template>

<script>
import KeyValueCard from '@/components/core/KeyValueCard.vue';
import { regions } from '@/utils/amazonPartner';
import { ourShipping, ourTotal, isMoreExpensive } from './pricing';

// Tabella della pagina "Analisi prezzi": solo presentazione, le azioni salgono al contenitore.
export default {
	name: 'PriceTable',
	components: { KeyValueCard },
	props: {
		items: { type: Array, default: () => [] },
		total: { type: Number, default: 0 },
		options: { type: Object, required: true },
		loading: { type: Boolean, default: false },
		mobileCards: { type: Boolean, default: false },
		// riga su cui si sta salvando la stellina
		followingId: { type: String, default: '' },
		// riga su cui gira "Analizza ora" e blocco mentre gira l'analisi completa (quota condivisa)
		analyzingId: { type: String, default: '' },
		analyzeDisabled: { type: Boolean, default: false },
	},
	data() {
		return {
			headers: [
				{ text: '', value: 'followed', sortable: false, width: 56 },
				{ text: '', value: 'image', sortable: false, width: 70 },
				{ text: 'Titolo', value: 'title', sortable: false },
				{ text: 'Seller Sku / ASIN', value: 'sellerSku', sortable: false },
				{ text: 'Nostro prezzo', value: 'price', sortable: false },
				{ text: 'Buybox', value: 'buybox', sortable: false },
				{ text: 'Prezzo buybox', value: 'buyBoxPrice', sortable: false },
				{ text: 'Analisi', value: 'analysisAt', sortable: false },
				{ text: '', value: 'actions', sortable: false, align: 'end' },
			],
		};
	},
	methods: {
		ourShippingOf: ourShipping,
		ourTotalOf: ourTotal,
		isMoreExpensive,
		money(value, currency) {
			if (value === null || value === undefined) {
				return '-';
			}
			return `${Number(value).toFixed(2)} ${currency || ''}`.trim();
		},
		hasBuyBoxPrice(item) {
			return !!(item.analysis && item.analysis.status === 'done' && item.analysis.buyBoxListingPrice !== null && item.analysis.buyBoxListingPrice !== undefined);
		},
		buyBoxChip(item) {
			const analysis = item.analysis || {};
			switch (analysis.status) {
				case 'done':
					return analysis.isOurs ? { text: 'Buybox nostra', color: 'success' } : { text: 'Buybox di altri', color: 'error' };
				case 'no_buybox':
					return { text: 'Nessuna buybox', color: 'grey darken-1' };
				case 'queued':
					return { text: 'In coda', color: 'info' };
				case 'error':
					return { text: 'Errore', color: 'error' };
				default:
					return item.followed ? { text: 'Da analizzare', color: 'grey' } : { text: 'Non seguito', color: 'grey lighten-1' };
			}
		},
		productUrl(item) {
			const region = regions.find(r => r.MarketplaceId === item.marketplaceId);
			return region && region.url && item.asin ? `${region.url}dp/${item.asin}` : '';
		},
		campiCard(item) {
			return [
				{ label: 'Seller Sku', value: item.sellerSku, strong: true },
				{ label: 'ASIN', value: item.asin || '-' },
				{
					label: 'Nostro prezzo',
					value: ourTotal(item) !== null ? `${this.money(item.price, item.currency)} + ${this.money(ourShipping(item), item.currency)} sped. = ${this.money(ourTotal(item), item.currency)}` : this.money(item.price, item.currency),
					strong: isMoreExpensive(item),
				},
			];
		},
	},
};
</script>

<style scoped>
/* vista card: niente layout a colonne, ogni riga e' un blocco e la card prende il 100% */
.table-cards ::v-deep table {
	display: block;
	width: 100%;
}
.price-cards,
.price-cards tr {
	display: block;
	width: 100%;
}
.price-cards tr,
.price-cards tr:hover {
	background: transparent !important;
}
.price-cards td {
	display: block;
	width: 100%;
	border-bottom: none !important;
	height: auto !important;
}
.price-cards tr:first-child td {
	padding-top: 12px !important;
}
.price-cards tr:last-child td {
	padding-bottom: 12px !important;
}
.price-cards tr + tr td {
	padding-top: 8px !important;
}
.card-label {
	font-size: 14px;
	color: rgba(0, 0, 0, 0.6);
}
</style>
